import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash, FaCheck, FaExclamation, FaEnvelope } from "react-icons/fa";
import { IoArrowBack } from "react-icons/io5";
import Swal from "sweetalert2";
import "bootstrap/dist/js/bootstrap.bundle.min";
import "bootstrap/dist/css/bootstrap.min.css";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);

  // ✅ change phoneNumber -> email
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");

  const [verificationCode, setVerificationCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [error, setError] = useState("");
  const [timer, setTimer] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // Timer countdown function
  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer((prevTimer) => prevTimer - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [timer]);

  // ✅ email validation
  const isEmailValid = () => {
    const value = email.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    return emailRegex.test(value);
  };

// Step 1: Send verification code - FIXED
const handleSendCode = async (e) => {
  e.preventDefault();
  if (timer > 0) return;

  setError("");
  setEmailError("");

  if (!email.trim()) {
    setEmailError("Email is required");
    return;
  }
  if (!isEmailValid()) {
    setEmailError("Please enter a valid email address");
    return;
  }

  setIsLoading(true);
  try {
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/api/auth/send-verification`,
      { email }
    );

    if (response.data.success) {
      setTimer(30);
      
      Swal.fire({
        icon: "success",
        title: "Success!",
        html: `Verification code has been sent to <strong>${email}</strong>`,
        timer: 3000,
        showConfirmButton: false,
      }).then(() => {
        setStep(2);
      });
    } else {
      // Show error message from backend
      Swal.fire({
        icon: "error",
        title: "Error",
        text: response.data.message || "Account not found",
      });
    }
  } catch (error) {
    console.error("Send code error:", error);
    
    // Handle different error statuses
    if (error.response?.status === 404) {
      Swal.fire({
        icon: "error",
        title: "Account Not Found",
        text: "No account exists with this email address. Please check your email or sign up.",
      });
    } else {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Failed to send verification code",
      });
    }
  } finally {
    setIsLoading(false);
  }
};

  // Step 2: Verify code
  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError("");

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/auth/verify-code`, // Changed from /api/verify-code
        {
          email,
          code: verificationCode,
        }
      );

      if (response.data.success) {
        setStep(3);
        setError(""); // Clear any previous errors
      } else {
        setError(response.data.message || "Invalid verification code");
      }
    } catch (error) {
      console.error("Verify code error:", error.response?.data);
      setError(error.response?.data?.message || "Invalid verification code");
    }
  };

  // Step 3: Reset Password
  // Step 3: Reset Password
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError("");

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/auth/reset-password`, // Changed from /api/reset-password
        {
          email,
          newPassword,
          verificationCode,
        }
      );

      if (response.data.success) {
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: "Password reset successfully!",
          timer: 2000,
          showConfirmButton: false,
        }).then(() => {
          navigate("/login");
        });
      } else {
        setError(response.data.message || "Failed to reset password");
      }
    } catch (error) {
      console.error("Reset password error:", error.response?.data);
      setError(error.response?.data?.message || "Failed to reset password");
    }
  };
  return (
    <div className="forgot-password-container d-flex justify-content-center align-items-center min-vh-100">
      <div className="forgot-password-form p-4 rounded-4 shadow">
        <h2 className="text-center mb-4 mt-3">Reset Password</h2>

        {step === 1 && (
          <form onSubmit={handleSendCode}>
            <div className="my-3 mt-5 position-relative">
              {/* Left email icon */}
              <span
                style={{
                  position: "absolute",
                  left: 16,
                  top: "50%",
                  transform: "translateY(-50%)",
                  opacity: 0.7,
                }}
              >
                <FaEnvelope />
              </span>

              <input
                type="email"
                className="form-control rounded-5 ps-5 py-2 border shadow-none"
                placeholder="Enter your email account"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (emailError) setEmailError("");
                }}
                required
              />

              {/* Right validation icon */}
              {email.length > 0 && (
                <span
                  style={{
                    position: "absolute",
                    right: 16,
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                >
                  {isEmailValid() ? (
                    <FaCheck className="text-success" />
                  ) : (
                    <FaExclamation className="text-danger" />
                  )}
                </span>
              )}

              {emailError && (
                <div className="text-danger mt-2 ps-2" style={{ fontSize: "0.9rem" }}>
                  {emailError}
                </div>
              )}
            </div>

            <button
              type="submit"
              className="btn forget-btn-color w-100 rounded-5 my-3 py-2 mb-md-2"
              disabled={timer > 0 || isLoading || (email.length > 0 && !isEmailValid())}
            >
              {isLoading
                ? "Sending..."
                : timer > 0
                  ? `Resend in ${timer}s`
                  : "Send Verification Code"}
            </button>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={handleVerifyCode}>
            <div className="mb-3">
              <input
                type="text"
                className="form-control rounded-5 ps-4 border border-dark mt-5 mt-md-0"
                placeholder="Enter verification code"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                required
              />
            </div>
            <button type="submit" className="btn forget-btn-color w-100 rounded-5 my-3 my-md-4">
              Verify Code
            </button>
          </form>
        )}

        {step === 3 && (
          <form onSubmit={handleResetPassword}>
            <div className="mb-3 position-relative">
              <input
                type={showPassword ? "text" : "password"}
                className="form-control rounded-5 ps-4 border-0 shadow-sm"
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
              <span
                className="password-toggle"
                style={{
                  position: "absolute",
                  right: 16,
                  top: "50%",
                  transform: "translateY(-50%)",
                  cursor: "pointer",
                }}
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <FaEye /> : <FaEyeSlash />}
              </span>
            </div>

            <div className="mb-3 position-relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                className="form-control rounded-5 ps-4 border-0 shadow-sm"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
              <span
                className="password-toggle"
                style={{
                  position: "absolute",
                  right: 16,
                  top: "50%",
                  transform: "translateY(-50%)",
                  cursor: "pointer",
                }}
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <FaEye /> : <FaEyeSlash />}
              </span>
            </div>

            <button type="submit" className="btn forget-btn-color w-100 rounded-5 mt-2 my-md-1">
              Reset Password
            </button>
          </form>
        )}

        {error && <div className="text-danger mt-3 text-center">{error}</div>}

        <div className="mt-2 text-start">
          <IoArrowBack
            className="back-arrow-icon cursor-pointer"
            onClick={() => navigate("/login")}
            size={24}
          />
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;

//Correct with 325 line code changes
